import { useState } from 'react';
import { useBillingStatus, useCreatePortal, friendlyErrorMessage } from 'markcat-api-client';
import styles from './GracePeriodBanner.module.css';

/** Show the full-width banner in the last N days of the grace window.
 *  PlanBadge already carries the ambient "ends in Nd" label across the
 *  entire grace period; the banner is reserved for urgent territory. */
const URGENT_DAYS = 3;

function daysUntil(expiresAt: number): number {
  const now = Math.floor(Date.now() / 1000);
  return Math.ceil((expiresAt - now) / 86400);
}

function secondsUntil(expiresAt: number): number {
  const now = Math.floor(Date.now() / 1000);
  return expiresAt - now;
}

/**
 * Sticky banner across the top of AppShell when the grace period has ≤ 3 days
 * left. Offers a one-click "Renew" that opens the Stripe Customer Portal in
 * the same tab so the user can resume or re-purchase the subscription.
 */
export function GracePeriodBanner() {
  const { data: status } = useBillingStatus();
  const createPortal = useCreatePortal();
  const [error, setError] = useState<string | null>(null);

  const expiresAt = status?.plan_expires_at ?? null;
  if (!expiresAt) return null;
  const seconds = secondsUntil(expiresAt);
  const days = daysUntil(expiresAt);
  if (days <= 0 || days > URGENT_DAYS) return null;

  const label =
    seconds <= 86400 ? 'Your subscription ends today.'
    : days === 2 ? 'Your subscription ends tomorrow.'
    : `Your subscription ends in ${days} days.`;

  const handleRenew = () => {
    setError(null);
    createPortal.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.url;
      },
      onError: (err) => setError(friendlyErrorMessage(err)),
    });
  };

  return (
    <div className={styles.banner} role="alert">
      <span className={styles.label}>
        <strong>{label}</strong>
        <span className={styles.sub}> Renew to keep your current plan active.</span>
      </span>
      <button
        type="button"
        className={styles.button}
        onClick={handleRenew}
        disabled={createPortal.isPending || !status?.customer_portal_available}
      >
        {createPortal.isPending ? 'Opening…' : 'Renew'}
      </button>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}
